import axios from 'axios';

import { API_BASE_URL } from './constants';
import auditLogService from '../services/auditLogService';
import { getUser } from '../utils/auth';
// deviceDetection modülü kullanılmadığı için import kaldırıldı

const instance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Mixed content sorununu çözmek için withCredentials ekledik
  withCredentials: false,
});

// API çağrılarını izlemek için zaman ölçümü yapacağız
const requestTimestamps = new Map<string, number>();

// Request interceptor
instance.interceptors.request.use(
  (config) => {
    // İstek zamanını kaydet ve X-Request-Id'yi ayarla
    // Header ismini kanonik hale getir: 'X-Request-Id'
    const h: any = config.headers;
    const getHeader = (key: string): string | undefined => {
      if (h && typeof h.get === 'function') {
        return h.get(key) || h.get(key.toLowerCase());
      }
      const obj = (config.headers || {}) as Record<string, any>;
      const found = Object.keys(obj).find((k) => k.toLowerCase() === key.toLowerCase());
      return found ? (obj as any)[found] : undefined;
    };
    const setHeader = (key: string, value: string) => {
      if (h && typeof h.set === 'function') {
        h.set(key, value);
      } else {
        if (!config.headers) config.headers = {} as any;
        (config.headers as any)[key] = value;
      }
    };

    let requestId: string | undefined = getHeader('X-Request-Id');
    if (!requestId) {
      requestId = Math.random().toString(36).substring(2, 15);
    }
    setHeader('X-Request-Id', requestId);

    // Zaman ölçümü için kaydet
    if (requestId) {
      requestTimestamps.set(requestId, Date.now());
    }
    
    // Login, register ve ayarlar endpoint'leri için token kontrolünü atla
    const url = config.url || '';
    
    // Login sayfasındaysak veya auth endpoint'lerine istek yapılıyorsa token kontrolünü atla
    if (url.includes('/auth/login') || url.includes('/auth/register')) {
      console.log(`🔓 Axios: ${url} için token kontrolü atlanıyor (auth endpoint)`);
      return config;
    }
    
    // Login sayfasında olsak bile, sadece auth endpoint'leri için atla; diğer tüm isteklerde token eklensin
    
    // Hem localStorage hem de sessionStorage'dan token'ı kontrol et
    // RememberMe ayarına göre öncelikli depolama alanını belirle
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    
    // Önce URL'i kontrol et ve debug için logla
    console.log(`🔎 API Çağrısı: ${config.url}`);
    
    // RememberMe true ise önce localStorage'a, false ise önce sessionStorage'a bak
    let token;
    if (rememberMe) {
      token = localStorage.getItem('token') || 
             localStorage.getItem('accessToken') ||
             localStorage.getItem('customerToken') ||
             sessionStorage.getItem('token') ||
             sessionStorage.getItem('accessToken') ||
             sessionStorage.getItem('customerToken');
    } else {
      token = sessionStorage.getItem('token') ||
             sessionStorage.getItem('accessToken') ||
             sessionStorage.getItem('customerToken') ||
             localStorage.getItem('token') || 
             localStorage.getItem('accessToken') ||
             localStorage.getItem('customerToken');
    }

    // URL parametresinden token mevcut mu?
    let urlToken: string | null = null;
    try {
      const params = new URLSearchParams(window.location.search);
      urlToken = params.get('token');
      // İsteğe bağlı: URL token'ını geçici depolarak kaydet (debug amaçlı)
      if (urlToken && urlToken.trim() !== '' && !sessionStorage.getItem('customerToken')) {
        sessionStorage.setItem('customerToken', urlToken);
      }
    } catch {
      // no-op
    }
    
    // Debug için token bilgisini logla
    if (token) {
      console.log(`🔐 Token bulundu (${rememberMe ? 'localStorage' : 'sessionStorage'} öncelikli), uzunluk: ${token.length}`);
    } else {
      console.warn('⚠️ Token bulunamadı! Kimlik doğrulama başarısız olabilir.');
    }
    
    // UserDatabase endpoint'i için özel kontrol
    if (config.url && (config.url.includes('/UserDatabase/') || config.url.includes('/user-databases/'))) {
      console.log('🔑 Veritabanı endpoint için token kontrolü yapılıyor...');
      // Her iki depolama alanından da token'ı kontrol et
      token = localStorage.getItem('accessToken') || 
              localStorage.getItem('token') ||
              localStorage.getItem('customerToken') ||
              sessionStorage.getItem('accessToken') || 
              sessionStorage.getItem('token') ||
              sessionStorage.getItem('customerToken');
      console.log(`🔑 UserDatabase API için token kontrolü: ${token ? 'Bulundu (uzunluk: ' + token.length + ')' : 'Bulunamadı'}`);
    }
    
    // Seçili veritabanı ID'sini al (eğer varsa)
    const selectedDatabaseId = localStorage.getItem('selectedDatabaseId');
    
    // Header yazımı: QR modu varsa X-Temp-Token kullan, Authorization'ı ekleme
    const setHeaderKV = (key: string, value: string) => {
      const hAny: any = config.headers;
      if (hAny && typeof hAny.set === 'function') hAny.set(key, value);
      else {
        if (!config.headers) config.headers = {} as any;
        (config.headers as any)[key] = value;
      }
    };
    const deleteHeader = (key: string) => {
      const hAny: any = config.headers;
      if (hAny && typeof hAny.delete === 'function') hAny.delete(key);
      else if (config.headers) {
        const found = Object.keys(config.headers as any).find(k => k.toLowerCase() === key.toLowerCase());
        if (found) delete (config.headers as any)[found];
      }
    };

    if (urlToken && urlToken.trim() !== '') {
      // QR token modu
      setHeaderKV('X-Temp-Token', urlToken);
      // Authorization'ı kaldır
      deleteHeader('Authorization');
    } else if (token) {
      // JWT modu
      setHeaderKV('Authorization', `Bearer ${token}`);
      if (token.length > 10) {
        console.log(`🔑 Token gönderiliyor: ${token.substring(0, 5)}...${token.substring(token.length - 5)}`);
      }
    } else {
      // Sadece auth gerektiren API'ler için uyarı göster
      if (!url.includes('/api/auth/') && !url.includes('/login') && !url.includes('/register')) {
        console.warn(`⚠️ Axios: ${url} için token bulunamadı!`);
      }
    }

    // Her iki modda da X-Database-Id ekleyelim (varsa)
    if (selectedDatabaseId) {
      setHeaderKV('X-Database-Id', selectedDatabaseId);
    }
    
    // API istek loglamayı devre dışı bırakıldı (kullanıcı talebi)
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
instance.interceptors.response.use(
  (response) => {
    // Yanıt süresini hesapla
    const rh: any = response.config.headers;
    const requestId: string | undefined = rh && typeof rh.get === 'function'
      ? (rh.get('X-Request-Id') || rh.get('x-request-id') || rh.get('X-Request-ID'))
      : (response.config.headers as any)?.['X-Request-Id'] || (response.config.headers as any)?.['x-request-id'] || (response.config.headers as any)?.['X-Request-ID'];
    const startTime = requestTimestamps.get(requestId as string);
    const endTime = Date.now();
    const responseTime = startTime ? endTime - startTime : 0;
    if (requestId) requestTimestamps.delete(requestId);
    
    // API yanıt loglamayı devre dışı bırakıldı (kullanıcı talebi)
    // Sadece yanıt süresini hesapla ve timestamp'i temizle
    const url = response.config.url || 'UNKNOWN_URL';
    
    return response;
  },
  (error) => {
    // Hata yanıt loglamayı devre dışı bırakıldı (kullanıcı talebi)
    // Sadece timestamp'i temizle
    if (error.config) {
      const eh: any = error.config.headers;
      const requestId: string | undefined = eh && typeof eh.get === 'function'
        ? (eh.get('X-Request-Id') || eh.get('x-request-id') || eh.get('X-Request-ID'))
        : (error.config.headers as any)?.['X-Request-Id'] || (error.config.headers as any)?.['x-request-id'] || (error.config.headers as any)?.['X-Request-ID'];
      if (requestId) requestTimestamps.delete(requestId);
    }
    
    if (error.response?.status === 401) {
      // Hangi endpoint'ten 401 hatası geldiğini gösterelim
      const url = error.config?.url || 'UNKNOWN_URL';
      const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
      console.warn(`🚫 Axios [${method}] ${url}: 401 Unauthorized hatası alındı`);
      
      // URL'de token parametresi var mı kontrol et
      const urlParams = new URLSearchParams(window.location.search);
      const tokenParam = urlParams.get('token');
      
      // Token parametresi yoksa normal login yönlendirmesi yap
      if (!tokenParam) {
        console.warn(`🔒 Axios: Token parametresi bulunamadı, tüm token anahtarları temizleniyor`);
        localStorage.removeItem('token');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('customerToken'); // customerToken'ı da temizleyelim
        sessionStorage.removeItem('token');
        // Yeniden yönlendirme yapmadan önce kısa bir gecikme ekleyelim
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      } else {
        // console.log(`🔓 Axios: URL'de token parametresi bulundu, login yönlendirmesi atlanıyor`);
      }
    }
    return Promise.reject(error);
  }
);

export default instance;
